import { MobileAppSidebar } from "@/components/mobile/mobile-app-sidebar";
import { MobileConnectionBanner } from "@/components/mobile/mobile-connection-banner";
import { MobileHeader } from "@/components/mobile/mobile-header";
import { NavUserDropdownMobile } from "@/components/mobile/user-nav-dropdown-mobile";
import { SidebarProvider } from "@/components/ui/sidebar";
import { MobileHeaderProvider } from "@/components/mobile/mobile-header-provider";
import { useChatOpenListener } from "@/hooks/useChatActions";
import { recordHistoryPosition } from "@/hooks/useCloseOnHistoryBack";
import { mobileScreenKey } from "@/lib/mobile-screen-key";
import { resetWindowTitle } from "@/lib/window-title";
import {
  companyOnShowId,
  companyPreviewPanelStore,
} from "@/stores/company-preview-panel-store";
import { ViewTransition, useEffect, useLayoutEffect, useRef } from "react";
import {
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";

/** What the chat reads from the navigation that opened it. */
export type MobileChatLocationState = {
  /** The company a question asked off the chat was about. */
  readonly companyIdTarget?: string;
};

export type LayoutMobileProps = {
  children?: React.ReactNode;
  showNavMain?: boolean;
};

export default function LayoutMobile({
  children,
  showNavMain = true,
}: LayoutMobileProps) {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  // A question asked off the chat — a company sheet's prompt, on a list or a
  // company page — opens the chat, which then sends it. Left alone, it would
  // wait in the buffer and go out unseen the next time the chat opened. The
  // company it was asked about goes along, so the conversation it starts
  // targets that company.
  useChatOpenListener(() => {
    if (mobileScreenKey(pathname) === "chat") return;
    const companyIdTarget =
      companyOnShowId(companyPreviewPanelStore.getSnapshot()) ??
      matchPath("/company/:companyId", pathname)?.params.companyId;
    companyPreviewPanelStore.close();
    const state: MobileChatLocationState = { companyIdTarget };
    navigate("/chat", { state });
  });

  // On the path, not the location: closing a sheet steps back through history
  // on the same URL, and must not wipe the title the screen set.
  useEffect(() => {
    resetWindowTitle();
  }, [pathname]);

  // Back and forward are left alone: iOS has already animated its swipe-back,
  // and a cross-fade on top of it would play the change twice. Set during the
  // commit — inside the view transition, before its animations exist — for
  // the CSS in index.css; a prop could not do it, the screen leaving keeps the
  // props of its last render.
  // Removed with the layout too (the desktop tree after a resize), so a POP
  // left set does not cancel the view transitions of the next one.
  useLayoutEffect(() => {
    document.documentElement.dataset.navigation = navigationType;
    return () => {
      delete document.documentElement.dataset.navigation;
    };
  }, [navigationType, pathname]);

  // Overlays read back from forward against the page the user was on.
  useLayoutEffect(() => {
    recordHistoryPosition();
  }, [key]);

  // A pushed page opens at its top, as a native one does — one company to the
  // next shares this scroller, one list to the next its own (marked
  // `data-mobile-scroller`): a screen kept mounted keeps its scroll otherwise.
  // Back and forward keep where the user was.
  const scrollerRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (navigationType !== "PUSH" || !scroller) return;
    scroller.scrollTop = 0;
    scroller
      .querySelectorAll<HTMLElement>("[data-mobile-scroller]")
      .forEach((screenScroller) => {
        screenScroller.scrollTop = 0;
      });
  }, [navigationType, pathname]);

  // The Claude app's shell: a header with the menu, the screen under it, and
  // the sidebar the menu opens — no tab bar. Screens set the header's title or
  // back arrow through `useMobileHeader`.
  return (
    <SidebarProvider>
      <MobileHeaderProvider>
        <div className="flex fixed inset-0 flex-col w-full h-dvh pt-[env(safe-area-inset-top,0px)] bg-background">
          {showNavMain ? (
            <MobileHeader />
          ) : (
            // Onboarding and the team screens have no navigation, but keep the
            // account, as the desktop does: sign out, switch team.
            <header className="flex shrink-0 justify-end items-center px-2 h-14">
              <NavUserDropdownMobile />
            </header>
          )}
          <MobileConnectionBanner />
          <main
            ref={scrollerRef}
            className="relative flex-1 min-h-0 overflow-auto overscroll-y-contain"
          >
            {/* Keyed by screen, so a navigation between screens cross-fades
                (the router already wraps it in a transition) while updates
                inside a screen — a streamed answer, a filter — never animate. */}
            <ViewTransition
              key={mobileScreenKey(pathname)}
              enter="auto"
              exit="auto"
              default="none"
            >
              {children ? children : <Outlet />}
            </ViewTransition>
          </main>
        </div>
        {showNavMain && <MobileAppSidebar />}
      </MobileHeaderProvider>
    </SidebarProvider>
  );
}
